/**
 * PostHog analytics event names and property interfaces.
 * Naming: {module}_{action}_{object?}
 * Properties: snake_case
 */

// --- Contact events ---
export const AnalyticsEvents = {
    contact_created: 'contact_created',
    contact_updated: 'contact_updated',
    contact_deleted: 'contact_deleted',
    contact_viewed: 'contact_viewed',
    contact_state_changed: 'contact_state_changed',
    contact_imported: 'contact_imported',
    contact_exported: 'contact_exported',
    contact_filtered: 'contact_filtered',
    contact_searched: 'contact_searched',

    // Notes
    note_created: 'note_created',
    note_updated: 'note_updated',
    note_deleted: 'note_deleted',
    note_imported: 'note_imported',

    // Agenda / Schedules
    agenda_viewed: 'agenda_viewed',
    schedule_created: 'schedule_created',
    schedule_updated: 'schedule_updated',
    schedule_deleted: 'schedule_deleted',

    // Opportunities
    opportunity_created: 'opportunity_created',
    opportunity_updated: 'opportunity_updated',
    opportunity_deleted: 'opportunity_deleted',
    opportunity_viewed: 'opportunity_viewed',
    opportunity_state_changed: 'opportunity_state_changed',
    opportunity_kanban_moved: 'opportunity_kanban_moved',
    opportunity_won: 'opportunity_won',
    opportunity_lost: 'opportunity_lost',

    // Campaigns
    campaign_created: 'campaign_created',
    campaign_updated: 'campaign_updated',
    campaign_deleted: 'campaign_deleted',
    campaign_started: 'campaign_started',
    campaign_completed: 'campaign_completed',
    campaign_contact_added: 'campaign_contact_added',
    campaign_contact_removed: 'campaign_contact_removed',

    // Email
    email_sent: 'email_sent',
    email_replied: 'email_replied',
    email_viewed: 'email_viewed',

    // WhatsApp
    whatsapp_message_sent: 'whatsapp_message_sent',
    whatsapp_message_received: 'whatsapp_message_received',
    whatsapp_conversation_viewed: 'whatsapp_conversation_viewed',
    whatsapp_audio_played: 'whatsapp_audio_played',

    // SMS & VoIP
    sms_sent: 'sms_sent',
    voip_call_made: 'voip_call_made',

    // Composer
    composer_opened: 'composer_opened',
    composer_channel_switched: 'composer_channel_switched',
    composer_cancelled: 'composer_cancelled',

    // Dashboard
    dashboard_viewed: 'dashboard_viewed',
    dashboard_widget_added: 'dashboard_widget_added',
    dashboard_widget_removed: 'dashboard_widget_removed',
    dashboard_widget_resized: 'dashboard_widget_resized',
    dashboard_widget_moved: 'dashboard_widget_moved',

    // Reports
    report_created: 'report_created',
    report_viewed: 'report_viewed',
    report_exported: 'report_exported',
    report_filter_applied: 'report_filter_applied',
    report_chart_viewed: 'report_chart_viewed',
    campaign_report_viewed: 'campaign_report_viewed',

    // Settings
    settings_accessed: 'settings_accessed',
    workspace_config_updated: 'workspace_config_updated',
    integration_configured: 'integration_configured',
    user_created: 'user_created',
    user_updated: 'user_updated',
    import_started: 'import_started',
    import_completed: 'import_completed',

    // Navigation
    page_viewed: 'page_viewed',
    command_search_used: 'command_search_used',
    quick_action_used: 'quick_action_used',

    // Session
    session_started: 'session_started',
    user_logged_in: 'user_logged_in',
    user_logged_out: 'user_logged_out',

    // Feature modules
    feature_module_accessed: 'feature_module_accessed',

    // Compass (global AI drawer)
    compass_drawer_opened: 'compass_drawer_opened',
    compass_tab_selected: 'compass_tab_selected',
    compass_feature_gate_viewed: 'compass_feature_gate_viewed',
    compass_chat_message_sent: 'compass_chat_message_sent',
    compass_user_profile: 'compass_user_profile',
    compass_chat_reset: 'compass_chat_reset',
    compass_chat_scope_switched: 'compass_chat_scope_switched',
    compass_workflow_triggered: 'compass_workflow_triggered',
    compass_workflow_trigger_failed: 'compass_workflow_trigger_failed',
    compass_suggestion_used: 'compass_suggestion_used',
    compass_execution_viewed: 'compass_execution_viewed',

    // Errors
    api_error: 'api_error',
    validation_error: 'validation_error',
    import_error: 'import_error',
    action_failed: 'action_failed'
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

// --- Property interfaces (optional, for type hints when tracking) ---

export interface ContactCreatedProps {
    source?: 'manual' | 'import' | 'leadlink';
    has_notes?: boolean;
    has_phone?: boolean;
    has_email?: boolean;
}

export interface ContactUpdatedProps {
    fields_changed?: string[];
    state_changed?: boolean;
}

export interface ContactDeletedProps {
    had_opportunities?: boolean;
    had_campaigns?: boolean;
}

export interface ContactViewedProps {
    view_source?: 'list' | 'search' | 'direct';
}

export interface ContactStateChangedProps {
    from_state?: string;
    to_state?: string;
    duration?: number;
}

export interface ContactImportedProps {
    record_count?: number;
    source?: string;
    mapping_used?: boolean;
}

export interface ContactExportedProps {
    record_count?: number;
    format?: 'excel' | 'pdf';
}

export interface ContactFilteredProps {
    filter_types?: string[];
    result_count?: number;
}

export interface ContactSearchedProps {
    search_type?: string;
    has_results?: boolean;
}

export interface OpportunityCreatedProps {
    source?: string;
    has_estimate?: boolean;
    has_products?: boolean;
    contact_linked?: boolean;
}

export interface OpportunityUpdatedProps {
    fields_changed?: string[];
    state_changed?: boolean;
}

export interface OpportunityDeletedProps {
    had_estimate?: boolean;
    value?: number;
}

export interface OpportunityViewedProps {
    view_type?: 'list' | 'kanban' | 'detail';
}

export interface OpportunityStateChangedProps {
    from_state?: string;
    to_state?: string;
    pipeline?: string;
    duration?: number;
}

export interface OpportunityKanbanMovedProps {
    from_column?: string;
    to_column?: string;
}

export interface OpportunityWonProps {
    value?: number;
    duration?: number;
    products_count?: number;
}

export interface OpportunityLostProps {
    loss_reason?: string;
    value?: number;
    duration?: number;
}

export interface CampaignCreatedProps {
    contacts_count?: number;
    type?: string;
}

export interface CampaignUpdatedProps {
    fields_changed?: string[];
}

export interface CampaignDeletedProps {
    contacts_count?: number;
    was_active?: boolean;
}

export interface CampaignStartedProps {
    contacts_count?: number;
}

export interface CampaignCompletedProps {
    contacts_count?: number;
    completion_rate?: number;
}

export interface CampaignContactAddedProps {
    addition_method?: 'manual' | 'bulk';
    count?: number;
}

export interface CampaignContactRemovedProps {
    count?: number;
}

export interface EmailSentProps {
    has_attachments?: boolean;
    recipient_type?: 'contact' | 'lead';
    from_context?: string;
}

export interface ComposerOpenedProps {
    channel_type?: string;
    context?: string;
}

export interface ComposerChannelSwitchedProps {
    from_channel?: string;
    to_channel?: string;
}

export interface ComposerCancelledProps {
    channel_type?: string;
    had_content?: boolean;
}

export interface DashboardWidgetProps {
    widget_type?: string;
    new_size?: string;
}

export interface ReportViewedProps {
    report_type?: string;
}

export interface ReportExportedProps {
    report_type?: string;
    format?: 'pdf' | 'excel';
}

export interface ReportFilterAppliedProps {
    filter_types?: string[];
    report_type?: string;
}

export interface ReportChartViewedProps {
    chart_type?: string;
    report_type?: string;
}

export interface SettingsAccessedProps {
    settings_area?: string;
}

export interface WorkspaceConfigUpdatedProps {
    config_type?: string;
}

export interface IntegrationConfiguredProps {
    integration_type?: 'leadlink' | 'whatsapp';
}

export interface UserCreatedProps {
    role?: string;
    has_modules?: boolean;
}

export interface ImportStartedProps {
    import_type?: string;
    record_count?: number;
}

export interface ImportCompletedProps {
    import_type?: string;
    success_count?: number;
    error_count?: number;
}

export interface PageViewedProps {
    page_path?: string;
    page_title?: string;
    previous_page?: string;
}

export interface CommandSearchUsedProps {
    search_term?: string;
    has_results?: boolean;
}

export interface QuickActionUsedProps {
    action_type?: string;
}

export interface SessionStartedProps {
    is_first_session?: boolean;
    days_since_last_visit?: number;
}

export interface UserLoggedInProps {
    login_method?: string;
}

export interface UserLoggedOutProps {
    session_duration?: number;
}

export interface FeatureModuleAccessedProps {
    module_name?: string;
}

export interface NoteCreatedProps {
    context?: 'contact' | 'opportunity';
    contact_id?: string;
    opportunity_id?: string;
}

export interface NoteImportedProps {
    record_count?: number;
    success_count?: number;
    error_count?: number;
}

export interface ScheduleCreatedProps {
    source?: 'contact' | 'opportunity';
    contact_id?: string;
    opportunity_id?: string;
    has_contact_id?: boolean;
    has_opportunity_id?: boolean;
}

export interface ApiErrorProps {
    endpoint?: string;
    status_code?: number;
    error_type?: string;
}

export interface ValidationErrorProps {
    form_name?: string;
    field_errors?: string[];
}

export interface ImportErrorProps {
    import_type?: string;
    error_count?: number;
    error_types?: string[];
}

export interface ActionFailedProps {
    action_type?: string;
    error_message?: string;
}
